import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { LabelWithValue } from '@/components/common/LabelWithValue';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { decideRequisition, requisitionQueryKeys } from '../api/productRequisition.api';
import {
  REQUISITION_STAGES,
  type RequisitionStage,
  type SpecimenRequisitionRow,
} from '../types/productRequisition.types';

const STAGE_LABELS: Record<RequisitionStage, string> = {
  REVIEW: 'Review',
  LEVEL_ONE: 'First Approval',
  LEVEL_TWO: 'Second Approval',
};

// Only the review settles a teacher quantity, and only review and the first
// approval settle a target sale — the same columns legacy writes per stage.
const CARRIES_TEACHER_QTY: Record<RequisitionStage, boolean> = {
  REVIEW: true,
  LEVEL_ONE: false,
  LEVEL_TWO: false,
};

const CARRIES_TARGET_SALE: Record<RequisitionStage, boolean> = {
  REVIEW: true,
  LEVEL_ONE: true,
  LEVEL_TWO: false,
};

const nextStage = (row: SpecimenRequisitionRow): RequisitionStage => {
  if (!row.isReview) return 'REVIEW';
  if (!row.isApprovedLevelOne) return 'LEVEL_ONE';
  return 'LEVEL_TWO';
};

const toQty = (value: string): number | undefined =>
  value.trim() === '' ? undefined : Number(value);

interface DecisionFormProps {
  row: SpecimenRequisitionRow;
  onDone: () => void;
}

function DecisionForm({ row, onDone }: DecisionFormProps) {
  const queryClient = useQueryClient();

  const [stage, setStage] = useState<RequisitionStage>(() => nextStage(row));
  const [qty, setQty] = useState(() => String(row.requiredQty));
  const [teacherQty, setTeacherQty] = useState(() => String(row.teacherQty));
  const [targetSaleQty, setTargetSaleQty] = useState(() => String(row.targetSaleQty));
  const [targetDate, setTargetDate] = useState(() => row.targetDate ?? '');

  const mutation = useMutation({
    mutationFn: (accept: boolean) =>
      decideRequisition(row.id, {
        stage,
        accept,
        qty: toQty(qty),
        ...(CARRIES_TEACHER_QTY[stage] ? { teacherQty: toQty(teacherQty) } : {}),
        ...(CARRIES_TARGET_SALE[stage] ? { targetSaleQty: toQty(targetSaleQty) } : {}),
        ...(targetDate === '' ? {} : { targetDate }),
      }),
    onSuccess: (_, accept) => {
      toast.success(
        accept ? `${STAGE_LABELS[stage]} recorded` : `${STAGE_LABELS[stage]} withdrawn`,
      );
      void queryClient.invalidateQueries({ queryKey: requisitionQueryKeys.all() });
      onDone();
    },
    onError: () => toast.error('Could not record that decision'),
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{row.instituteName}</DialogTitle>
        <DialogDescription>{row.specimenName}</DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <LabelWithValue label="Asked For">{row.requiredQty}</LabelWithValue>
          <LabelWithValue label="Raised By">{row.memberName}</LabelWithValue>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="requisition-stage">Stage</Label>
          <div className="flex gap-2" id="requisition-stage">
            {REQUISITION_STAGES.map((option) => (
              <Button
                key={option}
                type="button"
                size="sm"
                variant={stage === option ? 'default' : 'outline'}
                onClick={() => setStage(option)}
              >
                {STAGE_LABELS[option]}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="requisition-qty">Specimen Qty</Label>
          <Input
            id="requisition-qty"
            type="number"
            min={0}
            value={qty}
            onChange={(event) => setQty(event.target.value)}
          />
        </div>

        {CARRIES_TEACHER_QTY[stage] ? (
          <div className="flex flex-col gap-2">
            <Label htmlFor="requisition-teacher-qty">Teacher Qty</Label>
            <Input
              id="requisition-teacher-qty"
              type="number"
              min={0}
              value={teacherQty}
              onChange={(event) => setTeacherQty(event.target.value)}
            />
          </div>
        ) : null}

        {CARRIES_TARGET_SALE[stage] ? (
          <div className="flex flex-col gap-2">
            <Label htmlFor="requisition-target-sale">Target Sale Qty</Label>
            <Input
              id="requisition-target-sale"
              type="number"
              min={0}
              value={targetSaleQty}
              onChange={(event) => setTargetSaleQty(event.target.value)}
            />
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <Label htmlFor="requisition-target-date">Target Date</Label>
          <DatePickerInput
            id="requisition-target-date"
            value={targetDate}
            onChange={setTargetDate}
          />
        </div>
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => mutation.mutate(false)}
          disabled={mutation.isPending}
        >
          Withdraw
        </Button>
        <Button type="button" onClick={() => mutation.mutate(true)} disabled={mutation.isPending}>
          {STAGE_LABELS[stage]}
        </Button>
      </DialogFooter>
    </>
  );
}

interface RequisitionDecisionModalProps {
  row: SpecimenRequisitionRow | null;
  onClose: () => void;
}

export function RequisitionDecisionModal({ row, onClose }: RequisitionDecisionModalProps) {
  return (
    <Dialog
      open={row !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        {row === null ? null : <DecisionForm key={row.id} row={row} onDone={onClose} />}
      </DialogContent>
    </Dialog>
  );
}
